import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { X, User } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import pointsAPI from '../../services/pointsApi';
import referralAPI from '../../services/referralApi';
import { InterviewRecordAPI } from '../../services/interviewRecordApi';
import { formatBeijingDateTime, getApiTimestampMs, normalizeLastSeenMs, parseApiDateTime } from '../../utils/dateTimeBeijing';



const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: transparent;
  display: ${props => props.open ? 'block' : 'none'};
  z-index: 1000;
`;

const NotificationCard = styled.div`
  position: fixed;
  top: ${props => props.position?.top || '0'}px;
  left: ${props => props.position?.left || '0'}px;
  width: 240px; // 缩小卡片宽度
  max-height: ${props => (props.maxHeight ? `${props.maxHeight}px` : '320px')};
  background: white;
  border-radius: 6px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  border: 1px solid #e9ecef;
  transform: ${props => props.open ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(-10px)'};
  opacity: ${props => props.open ? '1' : '0'};
  transition: all 0.2s ease;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start; // 改为左对齐
  padding: 10px 14px;
  border-bottom: 1px solid #f1f3f4;
  background: #fafbfc;
  position: relative; // 添加相对定位
`;

const Title = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #2c3e50;
`;

const CloseBtn = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 3px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #f1f3f4;
    color: #495057;
  }
`;

const List = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: 280px; // 大约显示5条通知的高度，超出可滚动
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f3f4;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c7cd;
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8b3bc;
  }
`;

const Item = styled.div`
  display: flex;
  gap: 8px; // 减少图标和内容之间的间距
  padding: 8px 12px; // 减少内边距
  border-bottom: 1px solid #f1f3f4;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const IconWrap = styled.div`
  width: 24px; // 稍微减小图标容器
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #e9ecef;
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const Line1 = styled.div`
  font-size: 13px; // 稍微减小字体
  color: #2c3e50;
  font-weight: 500;
  line-height: 1.3; // 减少行高
  margin-bottom: 3px; // 减少底部间距
`;

const Line2 = styled.div`
  font-size: 11px; // 稍微减小字体
  color: #6c757d;
  line-height: 1.2; // 减少行高
  margin-bottom: 2px; // 添加底部间距
`;

const TimeText = styled.div`
  font-size: 10px; // 稍微减小字体
  color: #adb5bd;
  white-space: nowrap;
`;



function getNotificationTimestamp(ts) {
  return getApiTimestampMs(ts);
}

function formatTime(ts) {
  const d = parseApiDateTime(ts);
  if (!d) return String(ts || '-');
  return formatBeijingDateTime(d.toISOString()).replace(/-/g, '/');
}

// 将reason中的英文单词转换为中文
function translateReason(reason) {
  if (!reason) return reason;
  // 将常见的英文订阅周期转换为中文
  let translated = reason
    .replace(/订阅month充值/gi, '订阅月充值')
    .replace(/订阅week充值/gi, '订阅周充值')
    .replace(/订阅quarter充值/gi, '订阅季度充值')
    .replace(/month/gi, '月')
    .replace(/week/gi, '周')
    .replace(/quarter/gi, '季度');
  return translated;
}

// 删除字符串末尾的所有标点符号（。、！、？等）
function removePunctuation(text) {
  return text.replace(/[。，、！？；：]+$/g, '');
}

function buildPointsMessage(entry) {
  const change = entry.change;
  const abs = Math.abs(change);
  let reason = entry.reason || '';
  // 先转换中英文混杂的内容
  reason = translateReason(reason);
  
  if (reason === 'recharge') return removePunctuation(`充值成功，获得 ${abs} 积分`);
  if (reason === 'referral_success') return removePunctuation(`邀请成功奖励 +${abs} 积分`);
  if (reason === 'initial_bonus') return removePunctuation(`新用户赠送 +${abs} 积分`);
  if (reason === 'subscription_expired_clear' && change < 0) {
    return '会员已到期，本期积分已清零';
  }
  if (change < 0) {
    if (reason === 'resume_diagnosis') {
      return `简历诊断-功能使用扣减 ${abs} 积分`;
    }
    if (reason === 'interview_prep') {
      return `面试押题-功能使用扣减 ${abs} 积分`;
    }
    if (reason === 'mock_interview') {
      return `模拟面试-按时长扣减 ${abs} 积分`;
    }
    if (reason === 'meeting_assistant') {
      return `会议助手-按时长扣减 ${abs} 积分`;
    }
    if (reason === 'resume_pdf_export') {
      return `简历生成-PDF导出扣减 ${abs} 积分`;
    }
    return removePunctuation(`功能使用扣减 ${abs} 积分（${reason}）`);
  }
  if (change > 0) {
    // 处理"订阅month充值"等中英文混杂的情况
    if (reason.includes('订阅') && reason.includes('充值')) {
      return removePunctuation(`积分变更 +${abs}（${reason}）`);
    }
    return removePunctuation(`积分变更 +${abs}（${reason}）`);
  }
  return removePunctuation(`积分记录（${reason}）`);
}

const NotificationsPanel = ({ isOpen, onClose, position, onNewCountChange, prefetchedItems, maxHeight }) => {
  const { user } = useAuth();
  const userId = user?.user_id || null;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const lastSeenKey = useMemo(() => `notifications_last_seen_${userId || 'guest'}`, [userId]);

  // 当用户变化时，确保关闭通知面板，防止灰色蒙布
  useEffect(() => {
    if (!userId && isOpen && typeof onClose === 'function') {
      onClose();
    }
  }, [userId, isOpen, onClose]);

  // 初始打开时优先使用预取数据，避免"加载中..."
  useEffect(() => {
    if (isOpen && Array.isArray(prefetchedItems) && prefetchedItems.length > 0) {
      setItems(prefetchedItems);
    }
  }, [isOpen, prefetchedItems]);

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      if (!userId) return;
      setLoading(true);
      try {
        const [pointsRes, referralRes, recordsRes] = await Promise.allSettled([
          pointsAPI.getHistory(userId),
          referralAPI.getStats(userId).catch(() => null),
          InterviewRecordAPI.fetchInterviewRecordsRemote(userId).then(r => r.success ? (r.data || []) : [])
        ]);

        const list = [];

        if (pointsRes.status === 'fulfilled' && pointsRes.value?.entries) {
          const entries = pointsRes.value.entries || [];
          let running = pointsRes.value.balance ?? 0;
          for (const e of entries) {
            const balanceAfter = running;
            list.push({ 
              id: `points_${e.id}`, 
              type: 'points', 
              title: buildPointsMessage(e), 
              desc: `当前余额 ${balanceAfter} 积分`, 
              time: e.created_at 
            });
            running -= e.change;
          }
        }




        // 不再追加“邀请统计更新”类通知，避免与具体积分变动重复

        // 去重：优先根据 id，其次根据 title+time 组合键
        const seenIds = new Set();
        const seenKey = new Set();
        const deduped = [];
        for (const n of list) {
          const key = `${n.title}|${n.time}`;
          if (seenIds.has(n.id) || seenKey.has(key)) continue;
          seenIds.add(n.id);
          seenKey.add(key);
          deduped.push(n);
        }

        // 排序，保留所有通知（通过高度限制显示约5条，超出可滚动）
        deduped.sort((a, b) => getNotificationTimestamp(b.time) - getNotificationTimestamp(a.time));
        const latest = deduped; // 保留所有通知，不再限制数量

        setItems(latest);

        // 计算未读数量并上报给父组件
        let lastSeen = 0;
        try { lastSeen = normalizeLastSeenMs(parseInt(localStorage.getItem(lastSeenKey) || '0', 10) || 0); } catch {}
        const newCount = latest.filter(n => getNotificationTimestamp(n.time) > lastSeen).length;
        if (typeof onNewCountChange === 'function') onNewCountChange(newCount);
      } catch (e) {
        console.warn('加载通知失败', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, userId, lastSeenKey, onNewCountChange]);

  // 监听积分更新事件，实时刷新通知列表
  useEffect(() => {
    if (!userId) return;
    
    const handlePointsUpdate = async () => {
      // 如果通知面板是打开的，重新加载通知
      if (isOpen) {
        setLoading(true);
        try {
          const [pointsRes, referralRes, recordsRes] = await Promise.allSettled([
            pointsAPI.getHistory(userId),
            referralAPI.getStats(userId).catch(() => null),
            InterviewRecordAPI.fetchInterviewRecordsRemote(userId).then(r => r.success ? (r.data || []) : [])
          ]);

          const list = [];

          if (pointsRes.status === 'fulfilled' && pointsRes.value?.entries) {
            const entries = pointsRes.value.entries || [];
            let running = pointsRes.value.balance ?? 0;
            for (const e of entries) {
              const balanceAfter = running;
              list.push({ 
                id: `points_${e.id}`, 
                type: 'points', 
                title: buildPointsMessage(e), 
                desc: `当前余额 ${balanceAfter} 积分`, 
                time: e.created_at 
              });
              running -= e.change;
            }
          }


          // 去重
          const seenIds = new Set();
          const seenKey = new Set();
          const deduped = [];
          for (const n of list) {
            const key = `${n.title}|${n.time}`;
            if (seenIds.has(n.id) || seenKey.has(key)) continue;
            seenIds.add(n.id);
            seenKey.add(key);
            deduped.push(n);
          }

          deduped.sort((a, b) => getNotificationTimestamp(b.time) - getNotificationTimestamp(a.time));
          setItems(deduped);

          // 重新计算未读数量
          let lastSeen = 0;
          try { lastSeen = normalizeLastSeenMs(parseInt(localStorage.getItem(lastSeenKey) || '0', 10) || 0); } catch {}
          const newCount = deduped.filter(n => getNotificationTimestamp(n.time) > lastSeen).length;
          if (typeof onNewCountChange === 'function') onNewCountChange(newCount);
        } catch (e) {
          console.warn('刷新通知失败', e);
        } finally {
          setLoading(false);
        }
      }
    };

    window.addEventListener('pointsUpdated', handlePointsUpdate);
    return () => {
      window.removeEventListener('pointsUpdated', handlePointsUpdate);
    };
  }, [userId, isOpen, lastSeenKey, onNewCountChange]);

  const markAllRead = () => {
    try {
      // 使用当前所有通知中的最大时间戳，而不是当前时间
      // 这样可以确保所有当前显示的通知都被标记为已读
      if (items.length > 0) {
        const maxTime = Math.max(...items.map(n => {
          return getNotificationTimestamp(n.time);
        }));
        if (maxTime > 0) {
          localStorage.setItem(lastSeenKey, maxTime.toString());
        } else {
          // 如果没有有效的时间戳，使用当前时间
          localStorage.setItem(lastSeenKey, Date.now().toString());
        }
      } else {
        // 如果没有通知，使用当前时间
        localStorage.setItem(lastSeenKey, Date.now().toString());
      }
    } catch {}
  };

  const handleClose = () => {
    // 关闭时标记为已读并通知父组件清除红点
    markAllRead();
    if (typeof onNewCountChange === 'function') onNewCountChange(0);
    if (typeof onClose === 'function') onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <Overlay open={isOpen} onClick={handleClose} />
      <NotificationCard open={isOpen} position={position} maxHeight={maxHeight}>
        <Header>
          <Title>通知</Title>
          <CloseBtn onClick={handleClose}>
            <X size={16} />
          </CloseBtn>
        </Header>
        <List>
          {loading && items.length === 0 && (
            <Item>
              <IconWrap>
                <User size={14} color="#6c757d" />
              </IconWrap>
              <Content>
                <Line1>加载中...</Line1>
                <Line2>正在获取您的通知</Line2>
              </Content>
            </Item>
          )}
          {!loading && items.length === 0 && (
            <Item>
              <IconWrap>
                <User size={14} color="#6c757d" />
              </IconWrap>
              <Content>
                <Line1>暂无通知</Line1>
                <Line2>系统会在您有新的订阅、充值、功能使用或积分变动时提醒</Line2>
              </Content>
            </Item>
          )}
          {items.map(n => (
            <Item key={n.id}>
              <IconWrap>
                <User size={14} color="#6c757d" />
              </IconWrap>
              <Content>
                <Line1>{n.title}</Line1>
                {n.desc && <Line2>{n.desc}</Line2>}
                <TimeText>{formatTime(n.time)}</TimeText>
              </Content>
            </Item>
          ))}
        </List>
      </NotificationCard>
    </>
  );
};

export default NotificationsPanel;

